import React from 'react';
import { computed, observable, action, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import { Checkbox, mergeStyleSets, FontSizes } from '@fluentui/react';

import SearchableComboBox from 'src/components/base/SearchableComboBox';
import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';

import PushFlowstep from 'src/components/Flowsteps/PushData/PushFlowstep';
import Step from 'src/components/Flowsteps/base/Step';

import { 
  Type, generateFlowstepName, toPageTitle,
  InsertFlowsteps } from 'shared/utils/flowstep';
  
import * as Mapping from 'src/components/Flowsteps/PushData/Mapping';


const classNames = mergeStyleSets({
  errorMessage: {
    display: 'block',
    color: '#a4262c',
    fontSize: FontSizes.small,
    textAlign: 'center',
    width: '100%',
    marginTop: '4px',
  },
});


const Data = {
  HelpLinks: {
    [Type.Insert]: `https://www.xappex.com/docs/enabler4excel365/new-creating-new-records-in-salesforce/`,
    [Type.Update]: `https://www.xappex.com/docs/enabler4excel365/updating-existing-records-in-salesforce-4/`,
    [Type.Upsert]: `https://www.xappex.com/docs/enabler4excel365/using-upsert-to-update-or-create-records-in-salesforce-2/`,
  }
};


@inject('salesforceStore', 'dataStore', 'officeStore', 'userStore')
class InsertDataImpl extends PushFlowstep {
  @observable mappingManager = null;

  constructor(props) {
    super(props, { type: props.type, types: InsertFlowsteps });
    makeObservable(this);

    this.options = {
      ...this.options,
      bypassDuplicatesCheck: false,
    };

    this.mappingManager = new Mapping.Manager({ salesforceStore: this.props.salesforceStore }, { onSetDirty: this.setDirty });
    this.mappingManager.setType(props.type);

    this.flowstepTypesData = InsertFlowsteps.reduce((out, x) => Object.assign(out, {
      [x]: {
        dataTableText: `${x} data ${x === Type.Insert ? 'to' : 'in'}`,
        pageTitle: toPageTitle(x),
      },
    })
      , {});

    const allReactions = [...super.setupBaseReactions()];
    this.cleanReactions = () => allReactions.forEach(dispose => dispose());
  }

  @computed get _generatedName_() {
    const { worksheetName, targetCell } = this.selectionTarget || {};
    return generateFlowstepName({
      type: this.type,
      // sfObjectName: this.mappingOptions.sfObjectName,
      sfObjectName: this.mappingOptions.objectName,
      worksheetName, target: targetCell
    });
  }

  @computed get _isValid_() {
    return this.mappingManager?.isValid && this.areAllOptionsValid;
  }

  @computed get _helpArticleLink_() {
    return Data.HelpLinks[this.type];
  }

  @computed get typeDataTableText() {
    return this.flowstepTypesData[this.type].dataTableText;
  }

  @computed get objectOptions() {
    const selector = this.mappingManager.showLabels ? i => i.label : i => i.name;
    return (this.props ? this.props.officeStore.objects : []).map(i => ({ key: i.name, text: selector(i) }));
  }

  @computed get headersForResultIds() {
    return (this.excelHeaders || []).reduce((out, header) => {
      out.push({ key: header, text: header, excelColumnName: header });
      return out;
    }, [{ text: 'Not selected', excelColumnName: '' }]);
  }

  @computed get canOpenMappingPage() {
    return this.excelHeaders.length && this.mappingOptions.objectName;
  }

  @computed get mappingOptions() {
    return this.mappingManager?.mapping || {};
  }


  async _componentDidMountStart_(props) {
    await super._componentDidMountStart_(props);
    return this.props.officeStore.fetchObjects();
  }

  async _componentDidMountEnd_(task) {
    const objects = await task; 
    
    const { objectName } = this.mappingManager.mapping;
    if (!objects.find(x => x.name === objectName)) {
      if (this.flowstep) {
        this.officeStore.requestStore.raise(`Object '${objectName}' doesn't exist any more. Select another object and reapply mapping`);
      }

      this.mappingManager.setObjectName('');
    }
  }

  @action _setSelectionTarget_(selectionTarget) {
    super._setSelectionTarget_(selectionTarget);
    this.mappingManager.setExcelHeaders(this.excelHeaders);
  }

  @action _setType_(type) {
    this.mappingManager.setType(type);
  }

  async _tryLoadFlowstepOfDifferentType_(allWithSameTarget, allSimilarTypesWithSameTarget) {
    let soql = null;
    if (soql = allWithSameTarget.find(x => x.type === Type.Soql)) {
      // console.log('Loading similar soql flowstep: ', soql);
      // this.mappingOptions.sfObjectName = soql.options.object?.name ?? '';
      this.mappingManager.setObjectName(soql.options.object?.name ?? '');
      return;
    }
  }

  @action async _loadFlowstep_(flowstep, isExact) {
    const { mapping, bypassDuplicatesCheck } = flowstep.options;
    this.mappingManager.setMapping(mapping, { isExact });
    this.options = { ...this.options, bypassDuplicatesCheck };
    // this.mappingManager.loadMapping(mapping, { isExact });
    isExact && super.loadFlowstep(flowstep, isExact);
  }

  async _makeFlowstep_(makeCopy) {
    const flowstep = super.makeFlowstep(makeCopy);
    const mapping = this.mappingManager.makeMapping();
    const { bypassDuplicatesCheck } = this.options;
    Object.assign(flowstep.options, { mapping, bypassDuplicatesCheck });
    return flowstep;
  }

  _renderCustomSteps_ = ({ getStepTitle }) => {
    return (<>
      <Step title={ getStepTitle(`Select the object (table) to ${this.typeDataTableText}`) } >
        <SearchableComboBox
          placeholder="Select Object"
          selectedKey={this.mappingOptions.objectName}
          onChange={(_, item) => this.handleObjectChange(item.key)}
          disabled={!this.objectOptions.length || this._readOnly_}
          options={this.objectOptions}
        />
        <Checkbox label="Show Labels"
          checked={this.mappingManager.showLabels}
          onChange={(event, checked) => this.handleShowLabelsChange(checked)}
        />
      </Step>

      <Step title={ getStepTitle('Set up mappings') } >
        <Mapping.Button
          mappingManager={this.mappingManager}
          label={'Mappings'}
          isDisabled={!this.canOpenMappingPage}
          isEditingDisabled={this._readOnly_}
        />
      </Step>
    </>);
  }

  _renderFlowstepOptions_() {
    return this._renderCustomOptions_();
  }

  _renderCustomOptions_ = (renderProps) => 
    <>
      { !this.mappingManager.isUpdate &&
      <SearchableComboBox
        label={`Column to copy result IDs to`}
        placeholder={`Select column to copy result IDs to`}
        // selectedKey={this.mappingOptions.columnNameForResultIds || -1}
        selectedKey={this.mappingOptions.columnNameForResultIds}
        onChange={(event, item) => this.handleColumnNameForResultIdsChange(item.excelColumnName)}
        options={this.headersForResultIds}
        disabled={this._readOnly_}
      />
      }

      <CheckboxWithTooltip
        label="Load empty values"
        tooltip={'Check this box to be able to wipe out field values when the spreadsheet cell you are uploading is blank. This option is disabled by default for data safety reasons'}
        checked={this.mappingOptions.loadEmptyValues}
        disabled={this._readOnly_}
        onChange={(event, checked) => this.handleLoadEmptyValuesChange(checked)}
      />

      <CheckboxWithTooltip
        label={'Bypass Salesforce native duplicates check'}
        tooltip={'Check this box to bypass alerts and save duplicate records'}
        checked={this.options.bypassDuplicatesCheck}
        disabled={this._readOnly_}
        onChange={(event, checked) => super.handleOptionsChange('bypassDuplicatesCheck', checked)}
      />

      { super._renderCustomOptions_(renderProps) }
    </>


  handleShowLabelsChange = (value) => {
    this.mappingManager.showLabels = value;
  }

  @action handleObjectChange = (objectName) => {
    this.mappingManager.setObjectName(objectName);
  }

  @action handleColumnNameForResultIdsChange = (name) => {
    this.mappingManager.setColumnNameForResultIds(name);
  }

  @action handleLoadEmptyValuesChange = (value) => {
    this.mappingManager.setLoadEmptyValues(value);
  }
}


export const InsertData = (props) => <InsertDataImpl type={Type.Insert} { ...props } />;
export const UpdateData = (props) => <InsertDataImpl type={Type.Update} { ...props } />;
export const UpsertData = (props) => <InsertDataImpl type={Type.Upsert} { ...props } />;